/**
 * When a promo matching a configured base code (`codeToLookFor` on a tier row) is applied,
 * enforces the correct tier promocode for that campaign from cart subtotal.
 * Each distinct `codeToLookFor` is its own campaign. Tier promos are removed when no base
 * for that tier code is on the order. Runs on load, after promotion add/remove, and after
 * cart line changes.
 *
 * If the same `promocode` string is configured for multiple bases, a cart line is attributed
 * to a campaign only when exactly one of those bases is applied (avoids add/remove ping-pong).
 *
 * Revalidation is single-flight: events fired while a run is in progress coalesce into one
 * follow-up pass so rapid double-applies or checkout+cart listeners cannot interleave.
 *
 * @module Tavano.TTApplyPromoCodes.CartPromo
 */
define("Tavano.TTApplyPromoCodes.CartPromo", ["jQuery"], function (jQuery) {
  "use strict";

  const DEFAULT_TIERS = [
    {codeToLookFor: "USVSTHEM", minSubtotal: 5000, promocode: "USVSTHEM150"},
    {codeToLookFor: "USVSTHEM", minSubtotal: 1000, promocode: "USVSTHEM50"},
    {codeToLookFor: "USVSTHEM", minSubtotal: 500, promocode: "USVSTHEM20"},
    {codeToLookFor: "TESTPROMO123", minSubtotal: 300, promocode: "TESTPROMOEXTRA"}
  ];
  const DEFAULT_REVALIDATE_DELAY_MS = 400;

  function getConfig(environment) {
    return (environment && environment.getConfig && environment.getConfig()) || {};
  }

  function getTiers(environment) {
    const raw = getConfig(environment).TTApplyPromoCodes.cartPromo.tiers;
    let arr = Array.isArray(raw) ? raw : null;
    if (!arr && typeof raw === "string") {
      try {
        arr = JSON.parse(raw);
      } catch (e) {}
    }
    if (Array.isArray(arr) && arr.length) return arr;
    return DEFAULT_TIERS;
  }

  function getRevalidateDelayMs(environment) {
    const val = getConfig(environment).TTApplyPromoCodes.cartPromo.revalidateDelayMs;
    const num = typeof val === "number" ? val : parseInt(val, 10);
    return Number.isFinite(num) && num >= 0 ? num : DEFAULT_REVALIDATE_DELAY_MS;
  }

  /**
   * @param {Array} rawTiers
   * @returns {Array<{codeToLookFor: string, minSubtotal: number, promocode: string}>}
   */
  function normalizeTierRows(rawTiers) {
    const out = [];
    if (!Array.isArray(rawTiers)) return out;
    for (const t of rawTiers) {
      const codeToLookFor = typeof t.codeToLookFor === "string" ? t.codeToLookFor.trim().toUpperCase() : "";
      const promocode = typeof t.promocode === "string" ? t.promocode.trim().toUpperCase() : "";
      const minSubtotal = typeof t.minSubtotal === "number" ? t.minSubtotal : parseFloat(t.minSubtotal);
      if (!codeToLookFor || !promocode || !Number.isFinite(minSubtotal)) continue;
      out.push({codeToLookFor, minSubtotal, promocode});
    }
    return out;
  }

  function getNormalizedTiers(environment) {
    let n = normalizeTierRows(getTiers(environment));
    if (!n.length) n = normalizeTierRows(DEFAULT_TIERS);
    return n;
  }

  /**
   * @param {Array<{codeToLookFor: string, minSubtotal: number, promocode: string}>} normalizedRows
   * @returns {Map<string, Set<string>>}
   */
  function buildTierPromocodeToBases(normalizedRows) {
    const map = new Map();
    for (const row of normalizedRows) {
      if (!map.has(row.promocode)) map.set(row.promocode, new Set());
      map.get(row.promocode).add(row.codeToLookFor);
    }
    return map;
  }

  /**
   * @returns {Record<string, Array<{codeToLookFor: string, minSubtotal: number, promocode: string}>>}
   */
  function groupTiersByBase(normalized) {
    const byBase = {};
    for (const row of normalized) {
      if (!byBase[row.codeToLookFor]) byBase[row.codeToLookFor] = [];
      byBase[row.codeToLookFor].push(row);
    }
    for (const base of Object.keys(byBase)) {
      byBase[base].sort((a, b) => b.minSubtotal - a.minSubtotal);
    }
    return byBase;
  }

  function promoCodeUpper(p) {
    return (p && p.code ? String(p.code).trim() : "").toUpperCase();
  }

  function listHasBaseApplied(list, baseUpper) {
    return (list || []).some((p) => promoCodeUpper(p) === baseUpper);
  }

  function listHasPromoCode(list, codeUpper) {
    return (list || []).some((p) => promoCodeUpper(p) === codeUpper);
  }

  function anyBaseAppliedForTierPromo(list, promocodeUpper, tierPromocodeToBases) {
    const bases = tierPromocodeToBases.get(promocodeUpper);
    if (!bases) return false;
    for (const b of bases) {
      if (listHasBaseApplied(list, b)) return true;
    }
    return false;
  }

  function tierPromosForCampaign(list, baseCode, tierCodeSet, tierPromocodeToBases) {
    return (list || []).filter((p) => {
      const c = promoCodeUpper(p);
      if (!tierCodeSet.has(c)) return false;
      const bases = tierPromocodeToBases.get(c);
      if (!bases) return false;
      const applied = [...bases].filter((b) => listHasBaseApplied(list, b));
      return applied.length === 1 && applied[0] === baseCode;
    });
  }

  function getSummarySubtotal(summary) {
    if (!summary) return 0;
    const n = summary.subtotal;
    if (typeof n === "number" && Number.isFinite(n)) return n;
    const ex = summary.extras && summary.extras.discountedsubtotal;
    if (typeof ex === "number" && Number.isFinite(ex)) return ex;
    return 0;
  }

  function getCorrectTierPromocode(subtotal, rowsDesc) {
    const found = rowsDesc.find((t) => subtotal >= t.minSubtotal);
    return found ? found.promocode : null;
  }

  function removePromotionsChain(cart, promos) {
    let chain = jQuery.Deferred().resolve();
    promos.forEach((p) => {
      chain = chain.then(() => cart.removePromotion({promocode_internalid: String(p.internalid)}));
    });
    return chain;
  }

  function reconcileCampaign(cart, baseCode, rowsForBase, subtotal, promotions, tierPromocodeToBases) {
    const list = promotions || [];
    const tierCodeSet = new Set(rowsForBase.map((r) => r.promocode));

    const tierPromosApplied = tierPromosForCampaign(list, baseCode, tierCodeSet, tierPromocodeToBases);

    const defaultApplied = list.filter((p) => promoCodeUpper(p) === baseCode);

    if (defaultApplied.length > 1) {
      const extras = defaultApplied.slice(1);
      return removePromotionsChain(cart, extras).then(() => cart.getPromotions().then((fresh) => reconcileCampaign(cart, baseCode, rowsForBase, subtotal, fresh, tierPromocodeToBases)));
    }

    if (defaultApplied.length === 0) {
      const toRemove = list.filter((p) => {
        const c = promoCodeUpper(p);
        if (!tierCodeSet.has(c)) return false;
        return !anyBaseAppliedForTierPromo(list, c, tierPromocodeToBases);
      });
      if (toRemove.length === 0) return jQuery.Deferred().resolve();
      return removePromotionsChain(cart, toRemove);
    }

    const correctTierCode = getCorrectTierPromocode(subtotal, rowsForBase);
    const correctTierUpper = (correctTierCode || "").toUpperCase();

    const appliedTierCode = tierPromosApplied.length > 0 ? promoCodeUpper(tierPromosApplied[0]) : null;

    const wrongTier = tierPromosApplied.length > 0 && appliedTierCode !== correctTierUpper;
    const multipleTiers = tierPromosApplied.length > 1;
    const shouldRemoveAllTiers = !correctTierCode;

    if (wrongTier || multipleTiers || shouldRemoveAllTiers) {
      return removePromotionsChain(cart, tierPromosApplied).then(() =>
        cart.getPromotions().then((fresh) => {
          if (correctTierCode && !listHasPromoCode(fresh, correctTierUpper)) {
            return cart.addPromotion({promocode: correctTierCode});
          }
          return jQuery.Deferred().resolve();
        })
      );
    }

    if (tierPromosApplied.length === 0 && correctTierCode && !listHasPromoCode(list, correctTierUpper)) {
      return cart.addPromotion({promocode: correctTierCode});
    }

    return jQuery.Deferred().resolve();
  }

  function revalidatePromotions(cart, environment) {
    if (!cart) return jQuery.Deferred().resolve();

    const summaryDelayMs = 250;
    const waitForCart = function () {
      const d = jQuery.Deferred();
      setTimeout(() => d.resolve(), summaryDelayMs);
      return d.promise();
    };

    return waitForCart()
      .then(() => cart.getSummary())
      .then((summary) => {
        const subtotal = getSummarySubtotal(summary);
        const normalized = getNormalizedTiers(environment);
        const tierPromocodeToBases = buildTierPromocodeToBases(normalized);
        const groups = groupTiersByBase(normalized);
        const bases = Object.keys(groups).sort((a, b) => b.length - a.length);

        let chain = jQuery.Deferred().resolve();
        bases.forEach((baseCode) => {
          const rowsForBase = groups[baseCode];
          chain = chain.then(() =>
            jQuery
              .when(cart.getPromotions())
              .then(
                (promotions) => reconcileCampaign(cart, baseCode, rowsForBase, subtotal, promotions, tierPromocodeToBases),
                () => jQuery.Deferred().resolve()
              )
              .then(null, () => jQuery.Deferred().resolve())
          );
        });
        return chain;
      })
      .fail(() => jQuery.Deferred().resolve());
  }

  function debouncedRevalidate(enqueueRevalidate, environment) {
    const delayMs = getRevalidateDelayMs(environment);
    let timeout;
    return function () {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        enqueueRevalidate();
      }, delayMs);
    };
  }

  return {
    mountToApp: function mountToApp(container) {
      try {
        const cart = container.getComponent("Cart");
        const checkoutComponent = container.getComponent("Checkout");
        const environment = container.getComponent("Environment");

        if (!cart) return;

        const revalidateGate = {inFlight: null, pending: false};

        const enqueueRevalidate = function enqueueRevalidate() {
          if (revalidateGate.inFlight) {
            revalidateGate.pending = true;
            return revalidateGate.inFlight;
          }
          const p = revalidatePromotions(cart, environment);
          revalidateGate.inFlight = p;
          jQuery.when(p).always(() => {
            revalidateGate.inFlight = null;
            if (revalidateGate.pending) {
              revalidateGate.pending = false;
              enqueueRevalidate();
            }
          });
          return p;
        };

        const runRevalidate = debouncedRevalidate(enqueueRevalidate, environment);

        if (checkoutComponent) {
          checkoutComponent.on("afterShowContent", () => {
            enqueueRevalidate();
          });
        }

        cart.on("afterAddPromotion", runRevalidate);
        cart.on("afterRemovePromotion", runRevalidate);
        cart.on("afterAddLine", runRevalidate);
        cart.on("afterRemoveLine", runRevalidate);
        cart.on("afterUpdateLine", runRevalidate);
      } catch (e) {
        console.error("Tavano.TTApplyPromoCodes.CartPromo Error:", e);
      }
    }
  };
});
